import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import type { OrganizationScope, OrganizationScopeType } from "@/types/organization-scope"
import semver from "semver"
import { systemVersionAPI } from "@/api/teashop-base"
import { useAppStoreHook } from "@/store/modules/app"
import { API_GRAY_HEADER } from "@/utils/apiGrayHeader"
import { recordBusinessContextTrace } from "@/utils/businessContext/diagnostics"
import {
  getAppVersionCookie,
  getStableVersionCookie,
  restoreVersionCookies,
  setCookieWithNextVersion,
} from "@/utils/cache/cookies"
import {
  getLastSystemVersion,
  getNewestVersion,
  removeUpdateNoticeNeedShow,
  setLastSystemVersion,
  setNewestVersion,
} from "@/utils/cache/localStorage"
import { isFrontendArtifactReloadSupported } from "@/utils/frontendArtifactRouting"
import { claimFrontendArtifactReload } from "./artifactReloadGuard"

const WEB_SYSTEM_TYPE = 1
const SYSTEM_VERSION_REFRESH_TIMEOUT_MS = 5000
const ORGANIZATION_SCOPE_ID_PATTERN = /^(shop|chain-store|chain-headquarter|chain-region)-(\d+)$/

export { isFrontendArtifactReloadSupported } from "@/utils/frontendArtifactRouting"

export interface SystemVersionRuntimeGuard {
  isCurrent?: () => boolean
  skipBusinessContextBarrier?: boolean
}

export interface SystemVersionRuntimeRefreshResult {
  loaded: boolean
  source: "shop" | "brand" | "cache"
  shopId?: number
  brandId?: number
  reason?: "no-cached-version" | "invalid-shop-id" | "invalid-brand-id" | "empty-response" | "request-failed" | "stale-context"
}

export interface SystemVersionRuntimeLookupResult extends SystemVersionRuntimeRefreshResult {
  version?: SystemVersionDTO
}

function normalizeShopId(value: unknown) {
  const shopId = Number(value)
  return Number.isFinite(shopId) && shopId > 0 ? Math.trunc(shopId) : undefined
}

function normalizeBrandId(value: unknown) {
  const brandId = Number(value)
  return Number.isFinite(brandId) && brandId > 0 ? Math.trunc(brandId) : undefined
}

function resolveScopeType(scope: Pick<OrganizationScope, "id" | "type"> | undefined, scopeId?: string) {
  if (scope?.type) return scope.type
  const type = scopeId?.match(ORGANIZATION_SCOPE_ID_PATTERN)?.[1]
  return type as OrganizationScopeType | undefined
}

function resolveScopeShopId(scope: Pick<OrganizationScope, "id" | "type" | "meta"> | undefined, scopeId?: string) {
  const metaShopId = normalizeShopId(scope?.meta?.shopId)
  if (metaShopId) return metaShopId

  const normalizedScopeId = scopeId ?? scope?.id
  const [, type, id] = normalizedScopeId?.match(ORGANIZATION_SCOPE_ID_PATTERN) ?? []
  return type === "shop" || type === "chain-store" ? normalizeShopId(id) : undefined
}

function resolveScopeBrandId(scope: Pick<OrganizationScope, "id" | "type" | "meta"> | undefined, scopeId?: string) {
  const metaBrandId = normalizeBrandId(scope?.meta?.brandId)
  if (metaBrandId) return metaBrandId

  const normalizedScopeId = scopeId ?? scope?.id
  const [, type, id] = normalizedScopeId?.match(ORGANIZATION_SCOPE_ID_PATTERN) ?? []
  return type === "chain-headquarter" ? normalizeBrandId(id) : undefined
}

export function getCachedSystemVersion() {
  return getNewestVersion() || getLastSystemVersion()
}

export interface SystemVersionRuntimeSnapshot {
  current: SystemVersionDTO | null
  newest: SystemVersionDTO | null
  last: SystemVersionDTO | null
  appVersionCookie?: string
  stableVersionCookie?: string
}

export function captureSystemVersionRuntime(): SystemVersionRuntimeSnapshot {
  return {
    current: useAppStoreHook().currentSystemVersion,
    newest: getNewestVersion(),
    last: getLastSystemVersion(),
    appVersionCookie: getAppVersionCookie(),
    stableVersionCookie: getStableVersionCookie(),
  }
}

export function restoreSystemVersionRuntime(snapshot: SystemVersionRuntimeSnapshot) {
  restoreVersionCookies(snapshot.appVersionCookie, snapshot.stableVersionCookie)
  setNewestVersion(snapshot.newest ?? undefined)
  useAppStoreHook().setCurrentSystemVersion(snapshot.current)
  setLastSystemVersion(snapshot.last ?? undefined)
}

export function invalidateSystemVersionRuntime() {
  useAppStoreHook().setCurrentSystemVersion(null)
}

/**
 * Clears system-version-derived routing and update caches only. Authentication,
 * remembered organization/role choices and UI preferences are intentionally
 * outside this boundary.
 */
export function clearSystemVersionCache() {
  useAppStoreHook().setCurrentSystemVersion(null)
  setNewestVersion()
  setLastSystemVersion()
  removeUpdateNoticeNeedShow()
  restoreVersionCookies()
}

export function applySystemVersion(version?: SystemVersionDTO | null) {
  if (!version?.systemVersion) {
    return false
  }
  setCookieWithNextVersion(version)
  setNewestVersion(version)
  useAppStoreHook().keepUpdateToMyVersion()
  return true
}

export interface BusinessContextSystemVersionActivation {
  applied: boolean
  frontendReloadRequired: boolean
  frontendReloadSuppressed?: boolean
}

export function resolveFrontendModuleVersion(
  version?: SystemVersionDTO | null,
) {
  return version?.versionModuleRels
    ?.find(module => Number(module.moduleType) === 4)
    ?.moduleVersion
    ?.trim() ?? ""
}

/**
 * Activates request headers and runtime version without reloading midway
 * through a business-context transaction. The transaction owner finalizes the
 * deferred frontend reload only after scope, role and navigation are committed.
 */
export function activateBusinessContextSystemVersion(
  version?: SystemVersionDTO | null,
): BusinessContextSystemVersionActivation {
  if (!version?.systemVersion) {
    return {
      applied: false,
      frontendReloadRequired: false,
    }
  }
  setCookieWithNextVersion(version)
  setNewestVersion(version)
  const frontendReloadRequested = useAppStoreHook().keepUpdateToMyVersion({
    deferFrontendReload: true,
  })
  const frontendModuleVersion = resolveFrontendModuleVersion(version)
  const reloadSupported = isFrontendArtifactReloadSupported()
  const frontendReloadRequired = frontendReloadRequested
    && reloadSupported
    && claimFrontendArtifactReload(frontendModuleVersion)
  const frontendReloadSuppressed = frontendReloadRequested
    && reloadSupported
    && !frontendReloadRequired
  recordBusinessContextTrace("system-version.artifact-selected", {
    frontendModuleVersion,
    frontendReloadRequested,
    frontendReloadRequired,
    frontendReloadSuppressed,
    reloadSupported,
  })
  return {
    applied: true,
    // A loopback Vite preview owns only its local dist. Reloading it cannot
    // select the CDN artifact written to app_version and only restarts the
    // same bundle midway through login/scope switching.
    frontendReloadRequired,
    ...(frontendReloadSuppressed ? { frontendReloadSuppressed: true } : {}),
  }
}

export function finalizeBusinessContextSystemVersion(
  activation: BusinessContextSystemVersionActivation,
) {
  recordBusinessContextTrace("system-version.finalized", {
    applied: activation.applied,
    frontendReloadRequired: activation.frontendReloadRequired,
    frontendReloadSuppressed: activation.frontendReloadSuppressed,
    reloadSupported: isFrontendArtifactReloadSupported(),
  })
  if (activation.applied && activation.frontendReloadRequired) {
    recordBusinessContextTrace("system-version.page-reload-requested")
    window.location.reload()
    return
  }
  if (activation.frontendReloadSuppressed) {
    recordBusinessContextTrace("system-version.page-reload-suppressed")
    return
  }
  if (activation.applied) {
    useAppStoreHook().keepUpdateToMyVersion()
  }
}

export type SystemVersionNoticeType = 1 | 2

export interface SystemVersionNoticeResult {
  status: "ignored" | "stale" | "unchanged" | "preview" | "completed"
  version?: SystemVersionDTO
  updateType?: number
}

function isSameSystemVersion(currentVersion: string | undefined, nextVersion: string) {
  const current = semver.valid(currentVersion ?? "v0.0.0")
  const next = semver.valid(nextVersion)
  if (current && next) {
    return semver.eq(current, next)
  }
  return currentVersion === nextVersion
}

function isOlderSystemVersion(currentVersion: string | undefined, nextVersion: string) {
  const current = semver.valid(currentVersion ?? "")
  const next = semver.valid(nextVersion)
  return current && next ? semver.lt(next, current) : false
}

export function applySystemVersionNotice(
  version: SystemVersionDTO | undefined,
  noticeType: SystemVersionNoticeType,
  guard: SystemVersionRuntimeGuard = {},
): SystemVersionNoticeResult {
  if (!version?.systemVersion?.systemVersion) {
    return { status: "ignored" }
  }
  if (!isRuntimeGuardCurrent(guard)) {
    return { status: "stale" }
  }

  const appStore = useAppStoreHook()
  const nextVersion = version.systemVersion.systemVersion
  const currentVersion = appStore.currentSystemVersion?.systemVersion?.systemVersion
  if (isSameSystemVersion(currentVersion, nextVersion)) {
    setCookieWithNextVersion(version)
    return { status: "unchanged", version }
  }
  if (isOlderSystemVersion(currentVersion, nextVersion)) {
    return { status: "stale", version }
  }

  if (noticeType === 1) {
    const topNoticeContent = version.systemVersion.topNoticeContent
    if (topNoticeContent) {
      appStore.setShowTopNotice(true)
      appStore.setUpdateNoticeContent(topNoticeContent)
    }
    return { status: "preview", version }
  }

  setNewestVersion(version)
  setCookieWithNextVersion(version)
  return {
    status: "completed",
    version,
    updateType: version.systemVersion.noticeType,
  }
}

function isRuntimeGuardCurrent(guard: SystemVersionRuntimeGuard) {
  return guard.isCurrent?.() ?? true
}

export function applyCachedSystemVersion(
  guard: SystemVersionRuntimeGuard = {},
): SystemVersionRuntimeRefreshResult {
  if (!isRuntimeGuardCurrent(guard)) {
    return {
      loaded: false,
      source: "cache",
      reason: "stale-context",
    }
  }
  const cachedVersion = getCachedSystemVersion()
  if (!cachedVersion?.systemVersion) {
    return {
      loaded: false,
      source: "cache",
      reason: "no-cached-version",
    }
  }
  useAppStoreHook().setCurrentSystemVersion(cachedVersion)
  return {
    loaded: true,
    source: "cache",
  }
}

export async function loadSystemVersionForOrganizationScope(
  scope: Pick<OrganizationScope, "id" | "type" | "meta"> | undefined,
  scopeId = scope?.id,
  guard: SystemVersionRuntimeGuard = {},
): Promise<SystemVersionRuntimeLookupResult> {
  const scopeType = resolveScopeType(scope, scopeId)
  const isBrandScope = scopeType === "chain-headquarter" || scopeType === "chain-region"
  const brandId = isBrandScope ? resolveScopeBrandId(scope, scopeId) : undefined
  const shopId = scopeType === "shop" || scopeType === "chain-store"
    ? resolveScopeShopId(scope, scopeId)
    : undefined

  if (isBrandScope && !brandId) {
    return {
      loaded: false,
      source: "brand",
      reason: "invalid-brand-id",
    }
  }
  if (!isBrandScope && (scopeType === "shop" || scopeType === "chain-store") && !shopId) {
    return {
      loaded: false,
      source: "shop",
      reason: "invalid-shop-id",
    }
  }

  try {
    let res
    if (isBrandScope) {
      res = await systemVersionAPI.systemVersionGetUserVersionByBrandId({
        brandId: brandId!,
        systemType: WEB_SYSTEM_TYPE,
      }, {
        headers: {
          [API_GRAY_HEADER]: "true",
        },
        businessContextSystemVersion: null,
        ...(guard.skipBusinessContextBarrier ? { skipBusinessContextBarrier: true } : {}),
        skipGlobalErrorToast: true,
        timeout: SYSTEM_VERSION_REFRESH_TIMEOUT_MS,
      })
    } else if (shopId) {
      res = await systemVersionAPI.systemVersionGetUserVersionByShopId({
        shopId,
        systemType: WEB_SYSTEM_TYPE,
      }, {
        businessContextSystemVersion: null,
        ...(guard.skipBusinessContextBarrier ? { skipBusinessContextBarrier: true } : {}),
        skipGlobalErrorToast: true,
        timeout: SYSTEM_VERSION_REFRESH_TIMEOUT_MS,
      })
    }

    if (!res) {
      const cachedVersion = getCachedSystemVersion()
      return {
        loaded: Boolean(cachedVersion?.systemVersion),
        source: "cache",
        version: cachedVersion ?? undefined,
        reason: cachedVersion?.systemVersion ? undefined : "no-cached-version",
      }
    }
    if (!isRuntimeGuardCurrent(guard)) {
      return {
        loaded: false,
        source: isBrandScope ? "brand" : "shop",
        ...(brandId ? { brandId } : {}),
        ...(shopId ? { shopId } : {}),
        reason: "stale-context",
      }
    }

    const version = res.data.data
    return {
      loaded: Boolean(version?.systemVersion),
      source: isBrandScope ? "brand" : "shop",
      ...(brandId ? { brandId } : {}),
      ...(shopId ? { shopId } : {}),
      ...(version ? { version } : {}),
      reason: version?.systemVersion ? undefined : "empty-response",
    }
  } catch (error) {
    console.warn("获取当前机构系统版本失败:", error)
    return {
      loaded: false,
      source: isBrandScope ? "brand" : "shop",
      ...(brandId ? { brandId } : {}),
      ...(shopId ? { shopId } : {}),
      reason: "request-failed",
    }
  }
}

export async function refreshSystemVersionForShop(
  shopId: unknown,
  guard: SystemVersionRuntimeGuard = {},
): Promise<SystemVersionRuntimeRefreshResult> {
  const normalizedShopId = normalizeShopId(shopId)
  if (!normalizedShopId) {
    return {
      loaded: false,
      source: "shop",
      reason: "invalid-shop-id",
    }
  }

  try {
    const res = await systemVersionAPI.systemVersionGetUserVersionByShopId({
      shopId: normalizedShopId,
      systemType: WEB_SYSTEM_TYPE,
    }, {
      businessContextSystemVersion: null,
      skipGlobalErrorToast: true,
      timeout: SYSTEM_VERSION_REFRESH_TIMEOUT_MS,
    })
    if (!isRuntimeGuardCurrent(guard)) {
      return {
        loaded: false,
        shopId: normalizedShopId,
        source: "shop",
        reason: "stale-context",
      }
    }
    const loaded = applySystemVersion(res.data.data)
    return {
      loaded,
      shopId: normalizedShopId,
      source: "shop",
      reason: loaded ? undefined : "empty-response",
    }
  } catch (error) {
    console.warn("获取门店系统版本失败:", error)
    return {
      loaded: false,
      shopId: normalizedShopId,
      source: "shop",
      reason: "request-failed",
    }
  }
}

export async function refreshSystemVersionForBrand(
  brandId: unknown,
  guard: SystemVersionRuntimeGuard = {},
): Promise<SystemVersionRuntimeRefreshResult> {
  const normalizedBrandId = normalizeBrandId(brandId)
  if (!normalizedBrandId) {
    return {
      loaded: false,
      source: "brand",
      reason: "invalid-brand-id",
    }
  }

  try {
    const res = await systemVersionAPI.systemVersionGetUserVersionByBrandId({
      brandId: normalizedBrandId,
      systemType: WEB_SYSTEM_TYPE,
    }, {
      headers: {
        [API_GRAY_HEADER]: "true",
      },
      businessContextSystemVersion: null,
      skipGlobalErrorToast: true,
      timeout: SYSTEM_VERSION_REFRESH_TIMEOUT_MS,
    })
    if (!isRuntimeGuardCurrent(guard)) {
      return {
        loaded: false,
        brandId: normalizedBrandId,
        source: "brand",
        reason: "stale-context",
      }
    }
    const loaded = applySystemVersion(res.data.data)
    return {
      loaded,
      brandId: normalizedBrandId,
      source: "brand",
      reason: loaded ? undefined : "empty-response",
    }
  } catch (error) {
    console.warn("获取品牌系统版本失败:", error)
    return {
      loaded: false,
      brandId: normalizedBrandId,
      source: "brand",
      reason: "request-failed",
    }
  }
}

export async function refreshSystemVersionForOrganizationScope(
  scope: Pick<OrganizationScope, "id" | "type" | "meta"> | undefined,
  scopeId = scope?.id,
  guard: SystemVersionRuntimeGuard = {},
): Promise<SystemVersionRuntimeRefreshResult> {
  const scopeType = resolveScopeType(scope, scopeId)
  if (scopeType === "chain-headquarter" || scopeType === "chain-region") {
    return refreshSystemVersionForBrand(resolveScopeBrandId(scope, scopeId), guard)
  }
  if (scopeType === "shop" || scopeType === "chain-store") {
    return refreshSystemVersionForShop(resolveScopeShopId(scope, scopeId), guard)
  }
  return applyCachedSystemVersion(guard)
}
